import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getBalance } from '../../store/slices/Requestor/employeeInfoSlice'
import ValidationBanner from '../../components/common/ValidationBanner'
import { formatHours } from '../../utils/formatters'
import { getEnvironmentConfig } from '../../utils/detectEnv'

type Balance = {
  id: string
  title: string
  current?: string
  future?: string
  available?: string
  birthdayStatus?: string
  notAvailable?: boolean
  /** Whether to render the required asterisk on the Future Requests row. Only Vacation (PTO/ETO) carries this marker. */
  showAsterisk?: boolean
}

const BalanceCard: React.FC<{ card: Balance }> = ({ card }) => {
  const isBirthday = card.title === 'Birthday'
  return (
    <div className="p-4 tablet:p-3 mobile:p-2">
      <h3 className="h3 text-lg tablet:text-base mobile:text-sm">{card.title}</h3>
      {isBirthday ? (
        <p className="p3 -medium mt-2">{card.birthdayStatus || 'N/A'}</p>
      ) : (
        <>
          <p className="p3 -book m-0!">Current Balance {card.current}</p>
          <p className="p3 -book m-0! flex items-center gap-1!">
            - Future Requests
            {card.showAsterisk && (
              <span data-testid="future-asterisk" className='text-[#D00000]! leading-none relative'>*</span>
            )}
            <span>{card.future}</span>
          </p>
          <p className="p3 -medium m-0!">Available Balance <span className="text-[#003B71]">{card.available}</span></p>
        </>
      )}
    </div>
  )
}

const TimeOffBalance: React.FC = () => {
  const dispatch = useAppDispatch()
  const { data, loading, error } = useAppSelector(state => state.employeeInfo.timeOffBalance)
  const envConfig = getEnvironmentConfig() as any
  const configuredHrConnectUrl = envConfig.HRConnectUrl || ''
  // The active employee's name (not the logged-in user's — see employeeInfoSlice,
  // scoped by the same X-emp-nuid header the balance itself is fetched with) so
  // this title always matches whose balance is actually shown below it: the
  // logged-in user normally, or the selected employee while a Requestor Proxy
  // is acting on their behalf via Manual Time Off.
  const employeeName = useAppSelector(state => state.employeeInfo.employeeInfo.data?.employeeName)
  const manualSelectedNuid = useAppSelector(state => state.manualTimeOff?.selectedEmployee?.nuid)

  // Aborting on cleanup prevents an out-of-order response for a PREVIOUSLY
  // selected employee from overwriting the currently active employee's
  // balance — the reducer has no staleness check, so whichever response
  // arrives last wins regardless of which request was dispatched more
  // recently.
  useEffect(() => {
    const promise = dispatch(getBalance())
    return () => {
      promise?.abort?.()
    }
  }, [dispatch, manualSelectedNuid])

  const formatNumericValue = (value: number | null | undefined): string => {
    return value == null ? 'NA' : formatHours(value)
  }

  const isSentinelValue = (value: number | null | undefined, sentinel: number): boolean => {
    return value === sentinel
  }

  const buildBalances = (): Balance[] => {
    if (!data) {
      return []
    }

    const isVacationNa = isSentinelValue(data.vacation.available, 9999.99)
    const isFloatNa = isSentinelValue(data.float.available, 8888.88)
    const isFpdNa = isSentinelValue(data.fpd.available, 9999.99)

    return [
      {
        id: '1',
        title: 'Vacation (PTO/ETO)',
        current: isVacationNa ? 'NA' : formatNumericValue(data.vacation.current),
        future: formatHours(data.vacation.futureRequests),
        available: isVacationNa ? 'NA' : formatNumericValue(data.vacation.available),
        notAvailable: data.vacation.notAvailable,
        showAsterisk: true,
      },
      {
        id: '2',
        title: 'Float',
        current: isFloatNa ? 'NA' : formatNumericValue(data.float.current),
        future: formatHours(data.float.futureRequests),
        available: isFloatNa ? 'NA' : formatNumericValue(data.float.available),
        notAvailable: data.float.notAvailable,
      },
      {
        id: '3',
        title: 'Flexible Personal Days',
        current: isFpdNa ? 'NA' : formatNumericValue(data.fpd.current),
        future: formatHours(data.fpd.futureRequests),
        available: isFpdNa ? 'NA' : formatNumericValue(data.fpd.available),
        notAvailable: data.fpd.notAvailable,
      },
      {
        id: '4',
        title: 'Birthday',
        birthdayStatus: data.birthday?.eligible ? data.birthday?.status : 'N/A',
        notAvailable: !data.birthday?.eligible,
      },
    ]
  }

  // Transform API data to Balance format
  const balances: Balance[] = buildBalances()

  const validHrConnectUrl = configuredHrConnectUrl

  const notes = [
    {
      label: 'Current Balance',
      text: (
        <>
          reflects the hours in KPTIME. Please refer to {validHrConnectUrl ? (
            <a href={validHrConnectUrl} target="_blank" rel="noopener noreferrer" className="underline text-[#0074AD]">HRconnect</a>
          ) : (
            <span className="text-[#0074AD]">HRconnect</span>
          )} as the official system of record.
        </>
      ),
    },
    {
      label: 'Future Requests',
      text: 'equals future submitted requests plus future approved requests (Does not include Draft Requests).',
    },
    { label: 'Available Balance', text: 'equals Current Balance minus Future Requests.' },
    { label: 'PTO', text: 'equals Paid Time Off' },
    { label: 'ETO', text: 'equals Earned Time Off' },
  ]

  const notesSummary = notes.map((n) => (
    <p key={n.label} className="mt-2">
      <span className="p4 -medium">{n.label}</span>
      <span className="p4 -book ml-2"> {n.text}</span>
    </p>
  ))

  return (
    <div className="bg-[#F2F8FB] mt-9! p-5! w-full! rounded" id="balanceCont">
      <h2 className="h2 tablet:mt-8! mobile:mt-3.5!">
        My Time Off Balances{employeeName ? ` - ${employeeName}` : ''}
      </h2>

      {error && (() => {
        const randomNum = crypto.getRandomValues(new Uint32Array(1))[0] % 10000
        const errorId = `${globalThis.location.hostname}:jvm${randomNum}:${Date.now()}`
        return (
          <ValidationBanner
            variant="error"
            message={`We're having a technical problem. Please try again, or contact the Help Desk for assistance. Please give the Help Desk this Error ID: [${errorId}]`}
            compact={false}
          />
        )
      })()}

      {loading && <p className="mt-4">Loading balance...</p>}

      {!loading && !error && balances.length > 0 && (
        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4 gap-4 mb-6 mt-6">
          {balances.map((card) => (
            card.notAvailable ? null : <BalanceCard key={card.id} card={card} />
          ))}
        </div>
      )}

      <div className="mt-6! tablet:px-3 mobile:px-0">
        {notesSummary}

        <p className="p4 -book text-sm mt-4"><sup style={{ color: '#D00000' }}>*</sup>Vacation (PTO/ETO) – Future Requests does not include submitted Vacation Bidding requests, which are not yet approved.</p>
      </div>
    </div>
  )
}

export default TimeOffBalance
